/**
 * Server Connection Gate
 *
 * Blocking wrapper component that prevents app usage when the
 * SATCHEL API server is not available.
 *
 * Behavior:
 * - "checking" (initial): Shows loading spinner
 * - "connected": Renders children normally
 * - "disconnected": Shows full-screen blocking overlay
 */

import React from "react";
import { ASSET_PATHS } from "@satchel/core";
import { useServerConnection } from "../context/ServerConnectionContext";
import { DisconnectedOverlay } from "./DisconnectedOverlay";

export interface ServerConnectionGateProps {
  children: React.ReactNode;
}

/**
 * Loading Spinner shown during initial connection check
 */
const ConnectionCheckingSpinner: React.FC = () => (
  <div
    className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-sa-surface"
    role="status"
    aria-label="Checking server connection"
  >
    <div className="mb-6">
      <img
        src={ASSET_PATHS.SATCHEL_LOGO}
        alt="SATCHEL"
        className="h-20 w-20 rounded-lg animate-pulse"
      />
    </div>
    <div className="flex items-center gap-3 text-sa-text-dim">
      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <span>Connecting to SATCHEL server...</span>
    </div>
  </div>
);

/**
 * Server Connection Gate Component
 *
 * Must be used within ServerConnectionProvider.
 * Blocks the entire app UI when server is disconnected.
 */
export const ServerConnectionGate: React.FC<ServerConnectionGateProps> = ({ children }) => {
  const { status } = useServerConnection();

  // Initial connection check - show loading
  if (status === "checking") {
    return <ConnectionCheckingSpinner />;
  }

  // The overlay covers the app rather than replacing it. Rendering it instead of
  // children unmounts the whole tree, so a poll failure the server recovers from
  // seconds later still costs every piece of wallet and view state below.
  return (
    <>
      {children}
      {status === "disconnected" && <DisconnectedOverlay />}
    </>
  );
};

export default ServerConnectionGate;
